import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPasscodeListMembers, fetchPasscodeList } from '@/store/slices/passcode-lists';
import { selectPasscodeList } from '@/store/slices/passcode-lists/selectors';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectProjectEventId, getWorkingProjectUuid } from '@/store/slices/project/selectors';
import { PASSCODE_LIST_MEMBER_PAGE_SIZE } from '@/types/registration.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { isObject } from 'underscore';

export const useLoadPasscodeListMembers = (
  channel: number,
  passcodeListId: number,
  projectId?: number,
  projectUuid?: string,
  limit: number = PASSCODE_LIST_MEMBER_PAGE_SIZE,
  autoLoad: boolean = true
) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const selectorProjectId = useAppSelector(selectProjectEventId);
  const selectorProjectUuid = useAppSelector(getWorkingProjectUuid);
  const passcodeList = useAppSelector(selectPasscodeList);

  const finalProjectId = projectId ?? selectorProjectId ?? 0;
  const finalProjectUuid = projectUuid ?? selectorProjectUuid ?? '';

  const search = searchParams.get('search') || '';
  const pageIndex = parseInt(searchParams.get('page') || '1') - 1 || 0;
  const offset = pageIndex * limit;
  const sort = searchParams.get('sort') || '';
  const lists = searchParams.get('lists') || '';

  useEffect(() => {
    if (!channel || !passcodeListId) return;

    dispatch(fetchPasscodeList({ channel, passcodeListId }))
      .unwrap()
      .catch((e) => {
        if (isObject(e) && e.name === 'ConditionError') {
          // duplicate call, do nothing
          return;
        }

        toastTrigger('Error loading audience list', {
          type: EToastTypes.error,
          autoClose: 5000,
        });
      });
  }, [channel, dispatch, passcodeListId]);

  const reloadMembers = useCallback(async () => {
    if (!passcodeListId) return;

    try {
      await dispatch(
        fetchPasscodeListMembers({
          channel,
          projectId: finalProjectId,
          projectUuid: finalProjectUuid,
          passcodeListId,
          offset,
          limit,
          sort,
          search,
          lists,
        })
      );
    } catch (error) {
      console.error('Failed to load list members:', error);
    }
  }, [channel, dispatch, limit, lists, offset, passcodeListId, finalProjectId, finalProjectUuid, search, sort]);

  useEffect(() => {
    if (autoLoad) {
      reloadMembers();
    }
  }, [reloadMembers, autoLoad]);

  return { reloadMembers, passcodeList };
};
